import React, { useContext, useState, useEffect } from "react";
import { Box, Text, ResponsiveContext, Menu } from "grommet";
import AppLayout from "../../components/layouts/AppLayout";
import { formatDate, formatTime } from "../../utils/dates";
import { formatCurrency } from "../../utils/numbers";
import { silentFetcher, nonRedirectFetcher } from "../../utils/errors";
import { useRouter } from "next/router";
import { NotificationsContext } from "../../components/NotificationsProvider";
import useSWR, { useSWRConfig } from "swr";
import { syncNetSuite, syncShopify, syncHubSpot, pollSyncStatus, pollHubSpotSyncStatus } from "../../utils/connections";
import CustomerTag from "../../components/CustomerTag";
import { useSession } from "next-auth/react";
import CustomerLabel from "../../components/CustomerLabel";
import {
    faFileCirclePlus as IconFileAdd,
    faSync as IconSync,
    faChevronDown as IconArrowDown,
    faCircleExclamation as IconExclamation,
} from '@fortawesome/pro-light-svg-icons';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import ImportOrderModal from "../../components/draft-invoices/ImportOrderModal";
import { ThemeContext } from "styled-components";
import { ThickLeftBorderContainer } from "../../utils/styled";
import { normalizeColor } from "grommet/utils";
import DataTable from "../../components/DataTable";
import { DataTablePreferenceContext } from "../../components/DataTablePreferenceProvider";
import OrderTag from "../../components/OrderTag";
import { ExtensionButton, MainButtonWithExtension } from "../../utils/styled";
import MobileStickyBottomContainer from "../../components/MobileStickyBottomContainer";
import NewInvoiceMenu from "../../components/pages/draft-invoices/NewInvoiceMenu";
import OrdersList from "../../components/pages/draft-invoices/OrdersList";
import SyncButton, { getSyncMenuButtonProps } from "../../components/pages/draft-invoices/SyncButton";
import {isObjectEmpty} from "../../utils/object";
import DropButton from "../../components/DropButton";

const Invoices = (props) => {
    const {data: session} = useSession();
    const dataTablePreferenceCtx = useContext(DataTablePreferenceContext);
    const {sort: defaultSort} = dataTablePreferenceCtx.getPreference({table: 'invoices', tab:'failed'});
    const {company} = session || {};
    const [openImportOrderFromFileModal, setOpenImportOrderFromFileModal] = useState(false);
    const [isShopifySyncing, setIsShopifySyncing] = useState(false);
    const [isNetSuiteSyncing, setIsNetSuiteSyncing] = useState(false);
    const [isHubSpotSyncing, setIsHubSpotSyncing] = useState(false);
    const [ordersQueryUrl, setOrdersQueryUrl] = useState(''); // get the exact query url used to fetch order list, this is to ensure mutate() calls on the exact query url
    const [failedInvoiceSnapshot, setFailedInvoiceSnapshot] = useState([]);
    const router = useRouter();
    const { mutate } = useSWRConfig();
    const notificationCtx = useContext(NotificationsContext);

    // Integration-status widgets on the invoices list (reachable by invoice users
    // without settings access). Use nonRedirectFetcher so a 403 just hides the
    // integration UI instead of triggering the global 403 redirect to the dashboard.
    const {data: netsuiteSyncData} = useSWR("/api/settings/netsuite", nonRedirectFetcher, {
        revalidateOnFocus: false,
        onSuccess: data => setIsNetSuiteSyncing(data?.sync_in_progress)
    });

    const pollShopifySyncStatus = () => {
        let poll = setInterval(() => pollSyncStatus(notificationCtx.showNotification, (data) => {
            if (!data?.sync_in_progress) {
                setIsShopifySyncing(false);
                clearInterval(poll);
            }
        }), 1000);
    }

    const {data: shopifySyncData} = useSWR("/api/settings/shopify", nonRedirectFetcher, {
        revalidateOnFocus: false,
        onSuccess: data => {
            const syncInProgress = data?.some((oauth) => oauth?.sync_in_progress);
            if(syncInProgress){
                setIsShopifySyncing(syncInProgress);
                pollShopifySyncStatus();
            }
        }
    });

    const pollHubSpotSync = () => {
        let poll = setInterval(() => pollHubSpotSyncStatus(notificationCtx.showNotification, (data) => {
            if (!data?.sync_in_progress) {
                setIsHubSpotSyncing(false);
                clearInterval(poll);
            }
        }), 1000);
    }

    const {data: hubspotSyncData} = useSWR("/api/settings/hubspot", nonRedirectFetcher, {
        revalidateOnFocus: false,
        onSuccess: data => {
            if (data?.sync_in_progress) {
                setIsHubSpotSyncing(true);
                pollHubSpotSync();
            }
        }
    });

    const {data: failedInvoiceData} = useSWR(
        `/api/company/failed-invoices`,
        silentFetcher, {
            onSuccess: ({failed_invoices}) => {
                setFailedInvoiceSnapshot(failed_invoices);
            },
            revalidateOnFocus: false,
            revalidateIfStale: false
        }
    );

    const size = useContext(ResponsiveContext);
    const isMobile = size === "small";
    const isShopifyLinked = () => shopifySyncData && shopifySyncData.length > 0;
    const isNetSuiteLinked = () => netsuiteSyncData && netsuiteSyncData?.linked;
    const isHubSpotLinked = () => hubspotSyncData && hubspotSyncData?.connected;

    const failedInvoiceColumns = isMobile ? [
        {
            selector: row => row.id,
            cell: (datum) => {
                return (
                    <Box data-tag="allowRowEvents" fill="horizontal" justify="between" direction="row" gap="small" align="center" pad={{right: 'xsmall'}}>
                        <Box data-tag="allowRowEvents">
                            <CustomerLabel data-tag="allowRowEvents" customerId={datum?.recipient_id} />
                            <Text data-tag="allowRowEvents" size="small" color="text-weak">Invoice {datum?.identifier}{datum?.draft_series_id ? ' (Recurring)' : ''}</Text>
                        </Box>
                        <Box data-tag="allowRowEvents">
                            {formatCurrency(datum?.total_amount, datum?.currency)}
                        </Box>
                    </Box>
                )
            }
        }
    ] : [
        {
            selector: (row) => row.customer_name,
            name: "Customer",
            sortable: true,
            sortField: "company_name",
            cell: (datum) => datum?.recipient_id && <CustomerTag id={datum.recipient_id} inline />,
        },
        {
            selector: (row) => row.identifier,
            name: "Invoice #",
            sortable: true,
            sortField: "identifier",
            cell: (datum) => (<OrderTag id={datum?.id} />),
        },
        {
            selector: (row) => row.total_amount,
            name: "Amount",
            sortable: true,
            right: true,
            type: "currency",
            sortField: "total_amount",
            cell: (datum) => formatCurrency(datum.total_amount, datum.currency),
        },
        {
            selector: (row) => row.updated_at,
            name: "Last updated at",
            sortable: true,
            sortField: "updated_at",
            cell: (datum) => `${formatDate(datum.updated_at, false, true)}, ${formatTime(datum.updated_at)}`,
        },
    ];

    const failedInvoicesFrontEndSort = (field, direction) => {
        const failedInvoices = failedInvoiceData ? [...failedInvoiceData.failed_invoices] : [];
        switch (direction) {
        case 'asc':
            switch(field) {
            case 'customer_name':
                failedInvoices.sort((a,b) => a?.recipient?.display_name.localeCompare(b?.recipient?.display_name));
                break;
            case 'identifier':
                failedInvoices.sort((a,b) => a?.identifier.localeCompare(b?.identifier));
                break;
            case 'total_amount':
                failedInvoices.sort((a,b) => a?.total_amount - b?.total_amount);
                break;
            case 'updated_at':
                failedInvoices.sort((a,b) => new Date(a.updated_at) - new Date(b.updated_at));
                break;
            }
            break;
        case 'desc':
            switch(field) {
            case 'customer_name':
                failedInvoices.sort((a,b) => b?.recipient?.display_name.localeCompare(a?.recipient?.display_name))
                break;
            case 'identifier':
                failedInvoices.sort((a,b) => b?.identifier.localeCompare(a?.identifier));
                break;
            case 'total_amount':
                failedInvoices.sort((a,b) => b.total_amount - a.total_amount);
                break;
            case 'updated_at':
                failedInvoices.sort((a,b) => new Date(b.updated_at) - new Date(a.updated_at));
                break;
            }
        }
        if ((direction && field) && (defaultSort.direction !== direction || defaultSort.col !== field)) {
            dataTablePreferenceCtx.updatePreferences({
                table: 'invoices',
                tab: 'failed',
                key: 'sort',
                value: {direction, col: field}
            })
        }
        setFailedInvoiceSnapshot(failedInvoices);
    }

    useEffect(() => {
        defaultSort && failedInvoicesFrontEndSort(defaultSort.col, defaultSort.direction);
    }, [defaultSort]);

    const triggerSyncShopify = () => {
        setIsShopifySyncing(true);
        syncShopify(
            notificationCtx.showNotification,
            () => {}, //after sync is scheduled
            () => {
                //after sync is successful
                setIsShopifySyncing(false);
                mutate(ordersQueryUrl);
            }
        );
    }
    const triggerSyncNetSuite = () => {
        setIsNetSuiteSyncing(true);
        syncNetSuite(
            notificationCtx.showNotification,
            () => {}, //after sync is scheduled
            () => {
                //after sync is successful
                setIsNetSuiteSyncing(false);
                mutate(ordersQueryUrl);
            }
        );
    }
    const triggerSyncHubSpot = () => {
        setIsHubSpotSyncing(true);
        syncHubSpot(
            notificationCtx.showNotification,
            () => {}, //after sync is scheduled
            () => {
                //after sync is successful
                setIsHubSpotSyncing(false);
                mutate(ordersQueryUrl);
            }
        );
    }

    const getMobileSyncButtonProps = () => {
        const linkedIntegrations = [
            isShopifyLinked() && 'shopify',
            isNetSuiteLinked() && 'netsuite',
            isHubSpotLinked() && 'hubspot'
        ].filter(Boolean);

        if (linkedIntegrations.length === 0) return undefined;

        const anySyncing = isShopifySyncing || isNetSuiteSyncing || isHubSpotSyncing;

        // Only one integration connected
        if (linkedIntegrations.length === 1) {
            const integration = linkedIntegrations[0];
            const syncMap = {
                shopify: { trigger: triggerSyncShopify, syncing: isShopifySyncing, label: "Shopify" },
                netsuite: { trigger: triggerSyncNetSuite, syncing: isNetSuiteSyncing, label: "Netsuite" },
                hubspot: { trigger: triggerSyncHubSpot, syncing: isHubSpotSyncing, label: "HubSpot" },
            };
            const { trigger, syncing, label } = syncMap[integration];
            return {
                onClick: trigger,
                label: (
                    <Box direction="row" gap='medium' align="center">
                        <Box animation={syncing ? { type: "rotateRight", duration: 1350 } : undefined}>
                            <FontAwesomeIcon color='text-weak' SelectedIcon={IconSync} size="xsmall" />
                        </Box>
                        {syncing ? "Syncing" : `Sync ${label}`}
                    </Box>
                ),
                buttonProps: {
                    disabled: syncing,
                },
            }
        }
        // Multiple integrations connected
        const {onClick, label, ...rest} = getSyncMenuButtonProps({
            isShopifySyncing,
            isNetSuiteSyncing,
            isHubSpotSyncing,
            triggerSyncNetSuite,
            triggerSyncShopify,
            triggerSyncHubSpot,
            isShopifyLinked: isShopifyLinked(),
            isNetSuiteLinked: isNetSuiteLinked(),
            isHubSpotLinked: isHubSpotLinked(),
            shopifySyncData,
            netsuiteSyncData,
            hubspotSyncData,
            isMobile: true
        });
        return {
            component: Menu,
            onClick,
            label,
            buttonProps: rest
        }
    }

    return (
        <AppLayout
            hasStickyDesktopPageHeader
            title={"Invoices"}
            contentTitle="Draft"
            pageControls={
                !isMobile &&
                (<Box direction="row" gap='xsmall'>
                    <SyncButton
                        isShopifyLinked={isShopifyLinked()}
                        isNetSuiteLinked={isNetSuiteLinked()}
                        isHubSpotLinked={isHubSpotLinked()}
                        isShopifySyncing={isShopifySyncing}
                        isNetSuiteSyncing={isNetSuiteSyncing}
                        isHubSpotSyncing={isHubSpotSyncing}
                        triggerSyncNetSuite={triggerSyncNetSuite}
                        triggerSyncShopify={triggerSyncShopify}
                        triggerSyncHubSpot={triggerSyncHubSpot}
                        shopifySyncData={shopifySyncData}
                        netsuiteSyncData={netsuiteSyncData}
                        hubspotSyncData={hubspotSyncData}
                    />
                    <Box direction="row">
                        <MainButtonWithExtension
                            event={{section: 'draft_invoice_list_page_header', element: 'create_native_invoice'}}
                            button
                            label={<Text color="text">Create invoice</Text>}
                            icon={<FontAwesomeIcon color='text-weak' SelectedIcon={IconFileAdd} size="small" />}
                            secondary
                            hideRightBorder
                            onClick={() => {router.push("/draft-invoices/new/edit");}}
                        />
                        <ExtensionButton
                            event={{section: 'draft_invoice_list_page_header', element: 'create_invoice_extension'}}
                            responsive={false}
                            dropProps={{
                                align: { top: 'bottom', right: 'right' }
                            }}
                            secondary
                            icon={(<FontAwesomeIcon SelectedIcon={IconArrowDown} size="xxsmall" />)}
                            dropContent={
                                <NewInvoiceMenu
                                    shopifySyncData={shopifySyncData}
                                    onImportFromFileClicked={() => setOpenImportOrderFromFileModal(true)}
                                />
                            }/>
                    </Box>
                </Box>)
            }
            fullWidthHeader
            sidePanelProps={{ margin: "0", pad: "0" }}
        >
            {isMobile &&
                <MobileStickyBottomContainer
                    primaryButtonProps={{
                        label: 'Create invoice',
                        buttonProps: {
                            dropContent: (
                                <NewInvoiceMenu
                                    wrapperProps={{pad: 'small'}}
                                    {...(!isObjectEmpty(shopifySyncData) ? {} : {
                                        additionalMenuItems: [
                                            {
                                                label: 'Create invoice',
                                                onClick: (()=>{router.push("/draft-invoices/new/edit")})
                                            }
                                        ]
                                    })}
                                    shopifySyncData={shopifySyncData}
                                    onImportFromFileClicked={() => setOpenImportOrderFromFileModal(true)}
                                />),
                            dropProps: {align: {top: 'bottom'}},
                            event: {section: 'invoice_page_header', element: 'create'}
                        },
                        component: DropButton
                    }}
                    secondaryButtonProps={getMobileSyncButtonProps()}
                />
            }
            {
                openImportOrderFromFileModal && (
                    <ImportOrderModal
                        onClose={() => setOpenImportOrderFromFileModal(false)}
                    />
                )
            }
            {failedInvoiceData && failedInvoiceData?.total > 0 && (
                <ThemeContext.Consumer>
                    {(theme) => {
                        const borderColor = normalizeColor(theme.global.colors["border"], theme);
                        const color = "status-warning";
                        const accentColor = normalizeColor(theme.global.colors[color], theme);
                        return (
                            <ThickLeftBorderContainer
                                pad={{ horizontal: "medium" }}
                                margin={{ top: "small", bottom: "medium" }}
                                round={{ corner: 'left', size: 'xsmall' }}
                                borderColor={borderColor}
                                accentColor={accentColor}
                                elevation="medium"
                            >
                                <DataTable
                                    data={failedInvoiceSnapshot}
                                    columns={failedInvoiceColumns}
                                    title={
                                        <Box direction="row" gap="xsmall" align="center">
                                            <FontAwesomeIcon
                                                color="status-warning"
                                                SelectedIcon={IconExclamation}
                                                iconContainerProps={{
                                                    width: isMobile ? 'xxsmall' : 'auto',
                                                    margin: {right: isMobile ? 'small' : ''}
                                                }}
                                            />
                                            <Text>
                                                These invoices could not be auto-invoiced. Select an invoice to see what's going on.
                                            </Text>
                                        </Box>}
                                    hideExport={true}
                                    noTableHead={isMobile}
                                    onRowClicked={row => router.push(`/draft-invoices/${row.id}`)}
                                    dataTableWrapperProps={{hideDeepLink: true}}
                                    onSort={failedInvoicesFrontEndSort}
                                />
                            </ThickLeftBorderContainer>
                        );
                    }}
                </ThemeContext.Consumer>
            )}
            <OrdersList
                hasShopifyLinked={company?.has_shopify_linked}
                setOrdersQueryUrl={setOrdersQueryUrl}
                onOrderListChange={() => mutate(ordersQueryUrl)}
            />
        </AppLayout>
    );
}

export default Invoices;
